<template>
    <AhExportPreviewDialog
        ref="exportPreviewDialog"
        class="service-fee-statement"
        :visible="visible"
        title="勞報單匯出"
        :isExporting="isExporting"
        :progress="progress"
        :progressMessage="progressMessage"
        @open="onOpen"
        @close="onClose"
        @export="onExport"
    >
        <template slot="header-right">共 {{ snoList.length }} 份</template>
        <template slot="preview">
            <div class="preview-doc-list">
                <PdfPreviewPage1
                    id="service-fee-pdf-preview-page1"
                    :companyName="settings.companyName"
                    :title="settings.title"
                    :showSignArea="settings.drawSignArea"
                    :showIdImage="settings.drawIdImage"
                />
                <PdfPreviewPage2
                    id="service-fee-pdf-preview-page2"
                    v-if="settings.drawPassbookImage || settings.drawUnionInsuranceImage"
                    :showPassbookImage="settings.drawPassbookImage"
                    :showUnionInsuranceImage="settings.drawUnionInsuranceImage"
                />
            </div>
        </template>
        <div>
            <el-collapse v-model="activeNames">
                <el-collapse-item title="勞報單資訊" name="service-fee-basic">
                    <div style="display: flex; flex-direction: column; gap: 2px;">
                        <div class="setting-item">
                            <div class="label">公司名稱</div>
                            <el-input v-model="settings.companyName" size="small" :maxlength="20" show-word-limit></el-input>
                        </div>
                        <div class="setting-item" style="margin-bottom: 15px;">
                            <div class="label">標題</div>
                            <el-input v-model="settings.title" size="small" :maxlength="20" show-word-limit></el-input>
                        </div>
                        <div><el-checkbox v-model="settings.drawSignArea" @change="focusToPage($event, 1)"> 領款人簽收欄</el-checkbox></div>
                        <div><el-checkbox v-model="settings.drawIdImage" @change="focusToPage($event, 1)"> 證件正反面</el-checkbox></div>
                        <div><el-checkbox v-model="settings.drawPassbookImage" @change="focusToPage($event, 2)"> 存摺封面</el-checkbox></div>
                        <div><el-checkbox v-model="settings.drawUnionInsuranceImage" @change="focusToPage($event, 2)"> 職業工會投保證明</el-checkbox></div>
                    </div>
                </el-collapse-item>
            </el-collapse>
        </div>
    </AhExportPreviewDialog>
</template>
<script>
import moment from 'moment';
import { downloadBinaryFilesInZip } from '@/utils/ExportHelpers';
import {
    createPdfBlobByPdfMakerDefinition,
    ensureNotoSansTCFontsLoaded,
    isNotoSansTCFontsLoaded,
    startEstimatedProgressTicker,
} from '@/utils/PdfHelpers';
import ServiceFeePdfDef from '@/utils/PdfDefinitions/ServiceFeePdfDef';
import ServiceFeePdfFormater from '@/utils/PdfDataFormaters/ServiceFeePdfFormater';
import AhExportPreviewDialog from '@/components/ui/ahExportPreviewDialog.vue';
import PdfPreviewPage1 from './PdfPreviewPage1.vue';
import PdfPreviewPage2 from './PdfPreviewPage2.vue';

export default {
    name: 'payment-export-pdf-dialog',
    components: { AhExportPreviewDialog, PdfPreviewPage1, PdfPreviewPage2 },
    props: ['visible'],
    data() {
        return {
            snoList: [],
            isExporting: false,
            progress: 0,
            progressMessage: '',
            activeNames: ['service-fee-basic'],
            settings: {
                drawSignArea: true,
                drawIdImage: true,
                drawPassbookImage: false,
                drawUnionInsuranceImage: false,
                companyName: '',
                title: '勞務報酬單',
            },
        };
    },
    created() {},
    mounted() {},
    computed: {},
    methods: {
        setSnoList(snoList) {
            this.snoList = snoList;
        },
        onClose() {
            this.$emit('close');
        },
        onOpen() {
            this.settings.companyName = this.$store.getters['menu/companyInfo'].cH_CMP;
            this.resetProgress();
        },
        resetProgress() {
            this.progress = 0;
            this.processMessage = '';
        },
        focusToPage(checked, num) {
            if (checked) {
                this.scrollToId(`service-fee-pdf-preview-page${num}`);
            }
        },

        async fetchDocDetail(sno) {
            try {
                const resp = await this.$api_silent({
                    url: '/ServiceOrder/GetServiceFeeStatement',
                    method: 'GET',
                    params: { sno },
                });
                return resp.data.item;
            } catch (ex) {
                console.error('fetchDetailData error', ex);
                return null;
            }
        },

        async fetchBase64Images(sno, enableIdImage, enablePassbookDoc, enableUnionInsuranceDoc) {
            try {
                const resp = await this.$api_silent({
                    url: '/ServiceOrder/GetServiceFeeImageBase64',
                    method: 'GET',
                    params: {
                        sno,
                        IdCardFront: enableIdImage,
                        IdCardBack: enableIdImage,
                        PassbookDoc: enablePassbookDoc,
                        UnionInsuranceDoc: enableUnionInsuranceDoc,
                    },
                });
                return resp.data.item;
            } catch (ex) {
                console.error('fetchDetailData error', ex);
                return null;
            }
        },

        formatPdfFileName(data) {
            return `${data.payee}-勞報單-${data.systemNo}-${data.amt}${data.cny}.pdf`;
        },

        // 批次匯出 PDF 並下載
        /* eslint-disable no-await-in-loop */
        async onExport() {
            this.isExporting = true;

            // 設定參數
            const docCount = this.snoList.length; // 檔案個數

            // 阻擋無勾選
            if (docCount <= 0) {
                this.$message.error({ message: '無勾選的勞報單。', showClose: true });
                this.isExporting = false;
                return;
            }

            // 進度條權重：字型下載 = 1 單位（若已快取則為 0），每份勞報單 = 1 單位
            const fontWeight = isNotoSansTCFontsLoaded() ? 0 : 1;
            const totalWeight = fontWeight + docCount;
            const fontProgressSlice = (fontWeight / totalWeight) * 100;
            const progressStepWidth = (100 - fontProgressSlice) / docCount;

            // 初始化
            this.isExporting = true;
            this.progress = 0;
            this.progressMessage = '載入字型檔中...';

            // 字型下載期間用 rAF ticker 驅動進度條平滑前進
            let ticker = null;
            if (fontProgressSlice > 0) {
                ticker = startEstimatedProgressTicker({
                    estimatedMs: 5000,
                    onTick: (pct) => { this.progress = pct * fontProgressSlice; },
                });
            }

            await ensureNotoSansTCFontsLoaded(({
                label,
                index,
                total,
                received,
                contentLength,
            }) => {
                const mb = (received / 1048576).toFixed(1);
                if (contentLength) {
                    const totalMb = (contentLength / 1048576).toFixed(1);
                    const pct = Math.floor((received / contentLength) * 100);
                    this.progressMessage = `載入字型檔 ${label} (${index + 1}/${total}): ${mb} / ${totalMb} MB (${pct}%)`;
                    if (ticker) ticker.setExternal((index + received / contentLength) / total);
                } else {
                    this.progressMessage = `載入字型檔 ${label} (${index + 1}/${total}): ${mb} MB`;
                }
            });

            if (ticker) ticker.stop();
            this.progress = fontProgressSlice;
            this.progressMessage = '開始匯出';

            // 逐一處理每個檔案
            const pdfFiles = [];
            for (let i = 0; i < docCount; i += 1) {
                // 更新進度
                this.progress = fontProgressSlice + i * progressStepWidth;
                this.progressMessage = `勞報單（${i + 1}/${docCount}）：載入資料...`;

                // 取得檔案細節
                const detail = await this.fetchDocDetail(this.snoList[i]);
                if (detail) {
                    // 將細節資料做匯出格式化
                    console.log(detail);
                    const { companyName: cmpName, title } = this.settings;
                    const formatedDetail = ServiceFeePdfFormater(detail, cmpName, title);

                    // 取得檢附證件圖片 base64
                    const imgsToUse = {
                        idCardFrontBase64: null,
                        idCardBackBase64: null,
                        passbookDocBase64: null,
                        unionInsuranceDocBase64: null,
                    };
                    if (this.settings.drawIdImage || this.settings.drawPassbookImage || this.drawUnionInsuranceImage) {
                        this.progressMessage = `勞報單（${i + 1}/${docCount}）：下載證件圖檔...`;
                        const fetchedImgs = await this.fetchBase64Images(
                            this.snoList[i],
                            this.settings.drawIdImage,
                            this.settings.drawPassbookImage,
                            this.settings.drawUnionInsuranceImage,
                        );
                        if (fetchedImgs) {
                            // 身分證正反
                            if (this.settings.drawIdImage) {
                                imgsToUse.idCardFrontBase64 = fetchedImgs.idCardFront;
                                imgsToUse.idCardBackBase64 = fetchedImgs.idCardBack;
                            }
                            // 存摺封面
                            if (this.settings.drawPassbookImage) {
                                imgsToUse.passbookDocBase64 = fetchedImgs.passbookDoc;
                            }
                            // 工會投保證明
                            if (this.settings.drawUnionInsuranceImage) {
                                imgsToUse.unionInsuranceDocBase64 = fetchedImgs.unionInsuranceDoc;
                            }
                        }
                    }

                    // 使用 PdfMaker 產生 PDF 檔案
                    const pdf = await createPdfBlobByPdfMakerDefinition(ServiceFeePdfDef({
                        data: {
                            ...formatedDetail,
                            ...imgsToUse,
                        },
                        settings: {
                            pageSize: 'A4',
                            pageOrientation: 'portrait',
                            ...this.settings,
                        },
                    }));

                    // 加入到檔案陣列，供之後合併為 ZIP
                    pdfFiles.push({
                        name: this.formatPdfFileName(detail),
                        file: pdf,
                    });
                }
            }

            // 壓縮並下載
            this.progressMessage = '打包壓縮中...';

            // 當只有一個檔案時，就直接下載該 pdf
            // 當有多個檔案時，就壓縮成 zip 下載
            if (pdfFiles.length === 1) {
                const pdfFile = pdfFiles[0];
                const link = document.createElement('a');
                link.href = URL.createObjectURL(pdfFile.file);
                link.setAttribute('download', pdfFile.name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                downloadBinaryFilesInZip(`勞報單 ${moment().format('YYYY-MM-DD HH-mm-ss')}`, pdfFiles);
            }

            // 完成進度
            this.progress = 100;
            this.progressMessage = '';
            this.isExporting = false;
        },
    },
};
</script>
